/*
 * Copyright (c) 2020. Legalbird eine Marke der R&S Internet Jewels GmbH
 */

import React from "react";
import guidebookContentStyle from "../guidebookContentStyle";
import TextContainer from "../../Container/TextContainer";
import Layout from "../../../containers/Layouts/Layout";
import TeaserSlogan from "../../Teaser/TeaserSlogan";
import QuestionsContactSection from "../QuestionsContactSection/QuestionsContactSection";
import DoubleButton from "../../Button/DoubleButton";
import { Helmet } from "react-helmet-async";
import HighlightBox from "../../Box/HighlightBox/HighlightBox";
import { HashLink as Link } from "react-router-hash-link";
import backgroundTeaser from "../../../assets/img/guidebook/Teaser_Kuendigungsarten_700px.jpg";
import { gaSet } from "../../../services/cookieService";
import Typography from "@mui/material/Typography";
import { Box, Paper, Link as MuiLink } from "@mui/material";

const GuidebookKindsOfNotices = () => {
  const title = "Ratgeber - Arten der Kündigung | Legalbird";
  gaSet({ title: title });

  return (
    <Layout backgroundType={"serviceworld"}>
      <Helmet>
        <title>{title}</title>
        <meta
          name="description"
          content="Sie haben eine Kündigung erhalten und fragen sich, um welche Art der Kündigung es sich handelt, und ob sie
            rechtswirksam ist? Wir erklären es Ihnen."
        />
      </Helmet>

      <Paper sx={guidebookContentStyle.paper}>
        <TeaserSlogan slogan={"Die Arten der Kündigung"} backgroundImagePath={backgroundTeaser} height={300} headingColor={"#333333"} />
        <TextContainer>
          <p>
            Sie haben eine Kündigung erhalten und fragen sich, um welche Art der Kündigung es sich handelt, und ob sie rechtswirksam ist? Wir erklären Ihnen,
            welche Arten der Kündigung es gibt und wie diese begründet werden.
          </p>
          <hr />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Wann ist eine Kündigung wirksam?
          </Typography>
          <p>
            Unabhängig von der Art der Kündigung muss Ihr Arbeitgeber Fristen einhalten und eine gewisse Form der Übermittlung beachten, damit die Kündigung
            rechtswirksam ist. Folgende Voraussetzungen muss eine wirksame Kündigung erfüllen:
          </p>
          <ul>
            <li>die Kündigung wurde innerhalb der im Arbeitsvertrag oder Tarifvertrag festgehaltenen Kündigungsfrist ausgesprochen</li>
            <li>das Datum, zu dem gekündigt wird, muss enthalten sein</li>
            <li>die Kündigung wurde in Schriftform übermittelt</li>
            <li>die Kündigung wurde durch die richtige Person ausgesprochen</li>
            <li>der Kündigungsempfänger wurde korrekt ausgewählt</li>
            <li>es wurde gewährleistet, dass der Gekündigte auch Zugang zur Kündigung hat</li>
          </ul>
          <p>
            Wenn gegen die Kündigung geklagt wird, muss sie vom Arbeitgeber begründet werden. Mehr zu den Gründen einer Kündigung erfahren Sie{" "}
            <MuiLink component={Link} sx={guidebookContentStyle.link} to={"#kuendigungsgruende"}>
              hier
            </MuiLink>
            .
          </p>
          <hr />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Fristgerechte oder fristlose Kündigung?
          </Typography>
          <p>
            Es handelt sich um eine <strong>fristgerechte (oder auch ordentliche) Kündigung</strong>, wenn die Kündigung innerhalb der im Arbeitsvertrag oder Tarifvertrag
            festgehaltenen Kündigungsfrist ausgesprochen wurde.
          </p>
          <p>
            Wichtig zu wissen: Allgemein muss der Arbeitgeber die Kündigung nicht begründen. Wenn Sie länger als 6 Monate durchgehend beschäftigt waren und im
            Betrieb mehr als zehn Arbeitnehmer beschäftigt sind, gilt allerdings das Kündigungsschutzgesetz. In diesem Fall ist auch eine ordentliche Kündigung
            nur wirksam, wenn sie “sozial gerechtfertigt” ist. Das heißt, der Arbeitgeber kann nur aus personenbedingten, krankheitsbedingten,
            verhaltensbedingten oder betriebsbedingten Gründen kündigen.{" "}
            <MuiLink component={Link} sx={guidebookContentStyle.link} to={"#kuendigungsgruende"}>
              Mehr zu den Gründen für eine Kündigung erfahren Sie hier
            </MuiLink>
            .
          </p>
          <p>
            Anders ist es bei einer <strong>fristlosen (oder auch außerordentlichen) Kündigung</strong>. Wenn Ihnen fristlos gekündigt wurde, d.h. die Kündigungsfrist
            nicht eingehalten wurde, ist Ihr Arbeitgeber auf Anfrage dazu verpflichtet, die Kündigung zu begründen.
          </p>
          <HighlightBox headline={"In Kürze: Der Unterschied zwischen einer fristgerechten und fristlosen Kündigung"}>
            <p>
              Eine ordentliche Kündigung wird auch fristgerechte Kündigung genannt, da sie innerhalb der vertraglich vereinbarten Kündigungsfrist ausgesprochen
              wird. Bei einer außerordentlichen Kündigung wird fristlos, sprich ohne Beachtung der Kündigungsfrist gekündigt. Eine außerordentliche Kündigung
              ist nur rechtswirksam, wenn Sie aus wichtigem Grund ausgesprochen wird.
            </p>
          </HighlightBox>
          <Box sx={guidebookContentStyle.scrollToAnchor} id={"kuendigungsgruende"} />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Gründe für eine Kündigung
          </Typography>
          <p>
            Sobald ein Unternehmen mehr als zehn Angestellte hat und das Arbeitsverhältnis länger als 6 Monate andauert oder eine fristlose Kündigung
            ausgesprochen wird, gilt das Kündigungsschutzgesetz. Mit diesem Gesetz soll sicher gestellt werden, dass dem Arbeitnehmer nicht ungerechtfertigt
            gekündigt werden kann. Das Kündigungsschutzgesetz gibt vor, dass eine Kündigung “sozial gerechtfertigt” sein muss. Eine Kündigung ist sozial
            gerechtfertigt, wenn sie wie folgt begründet werden kann:
          </p>
          <ul>
            <li>
              durch das Verhalten des Arbeitnehmers -{" "}
              <MuiLink component={Link} sx={guidebookContentStyle.link} to={"#verhaltensbedingte-kuendigung"}>
                verhaltensbedingte Kündigung
              </MuiLink>
            </li>
            <li>
              durch die Person -{" "}
              <MuiLink component={Link} sx={guidebookContentStyle.link} to={"#personenbedingte-kuendigung"}>
                personenbedingte Kündigung
              </MuiLink>
            </li>
            <li>
              durch Erkrankung der Person -{" "}
              <MuiLink component={Link} sx={guidebookContentStyle.link} to={"#krankheitsbedingte-kuendigung"}>
                krankheitsbedingte Kündigung
              </MuiLink>
            </li>
            <li>
              oder durch betriebliche Gegebenheiten -{" "}
              <MuiLink component={Link} sx={guidebookContentStyle.link} to={"#betriebsbedingte-kuendigung"}>
                betriebsbedingte Kündigung
              </MuiLink>
            </li>
          </ul>
          <p>
            Zusätzlich zu diesen sogenannten “Beendigungskündigungen” gibt es noch die Möglichkeit, dass Sie ein Angebot für ein anderes Arbeitsverhältnis -
            also eine{" "}
            <MuiLink component={Link} sx={guidebookContentStyle.link} to={"#aenderungskuendigung"}>
              Änderungskündigung
            </MuiLink>{" "}
            - erhalten.
          </p>
          <hr />
          <Box sx={guidebookContentStyle.scrollToAnchor} id={"verhaltensbedingte-kuendigung"} />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Verhaltensbedingte Kündigung
          </Typography>
          <p>Verhaltensbedingte Kündigungsgründe können in folgende Gruppen unterteilt werden:</p>
          <ul>
            <li>Schlecht- oder Fehlleistungen (auch Pflichtwidrigkeiten im Leistungsbereich genannt)</li>
            <li>Verstöße gegen die betriebliche Ordnung (z.B. Verstöße gegen ein Rauchverbot)</li>
            <li>
              Störungen im Vertrauensbereich (z.B. Vollmachtsmissbrauch oder überhöhte Spesenabrechnungen)
              <br />
              Hinweis: Fehlverhalten in diesem Bereich stellen in der Regel auch einen wichtigen Grund für eine außerordentliche Kündigung dar
            </li>
            <li>Verletzungen von arbeitsvertraglichen Nebenpflichten (z.B. Verstöße gegen die Gehorsams-, Treue-, und Geheimhaltungspflicht)</li>
            <li>
              außerdienstliches Verhalten (z.B. strafbare Handlungen, die das Arbeitsverhältnis konkret beeinträchtigen, z.B. wenn ein Bankkassierer eine
              Betrugsstraftat begeht.)
            </li>
          </ul>
          <p>Wichtig zu wissen: Eine verhaltensbedingte Kündigung ist nur rechtmäßig, wenn zuvor Abmahnungen ausgesprochen wurden!</p>
          <hr />
          <Box sx={guidebookContentStyle.scrollToAnchor} id={"personenbedingte-kuendigung"} />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Personenbedingte Kündigung
          </Typography>
          <p>
            Ein Arbeitsverhältnis beruht darauf, dass der Arbeitnehmer eine Arbeitsleistung erbringt, die durch eine Gegenleistung (in Form einer Vergütung)
            durch den Arbeitgeber ausgeglichen wird. Wenn der Arbeitnehmer die vereinbarte Arbeitsleistung nicht erbringen kann, muss der Arbeitgeber daher die
            Möglichkeit erhalten, das Arbeitsverhältnis aufzulösen.
          </p>
          <p>
            Eine personenbedingte Kündigung beruht auf den persönlichen Eigenschaften, Fähigkeiten und Fertigkeiten des Arbeitnehmers. Die Kündigungsgründe sind
            also dadurch gekennzeichnet, dass der Arbeitnehmer von seinen persönlichen Voraussetzungen her die Fähigkeit und Eignung verloren (oder nie
            besessen) hat, die geschuldete Arbeitsleistung zu erbringen. Der weitaus{" "}
            <strong>häufigste Fall der personenbedingten Kündigung ist eine Krankheit des Arbeitnehmers</strong>. Die{" "}
            <MuiLink component={Link} sx={guidebookContentStyle.link} to={"#krankheitsbedingte-kuendigung"}>
              krankheitsbedingte Kündigung
            </MuiLink>{" "}
            ist daher ein Sonderfall der personenbedingten Kündigung.
          </p>
          <p>
            Beispiel für einen selbstverschuldeten persönlichen Kündigungsgrund:
            <br />
            Ein LKW-Fahrer verliert wegen einer Trunkenheitsfahrt mit anschließendem Unfall für anderthalb Jahre die Fahrerlaubnis. Wenn der Arbeitgeber keine
            Möglichkeit hat, ihn anderweitig (z.B. im Büro) zu beschäftigen, ist eine Überbrückung dieser langen Zeit für den Arbeitgeber nicht zumutbar.
            Schließlich hat der Arbeitnehmer den Kündigungsgrund selbst verschuldet.
          </p>
          <p>
            Das Fehlen der Fähigkeit des Arbeitnehmers seine Arbeit zu leisten ist in vielen Fällen vom Arbeitnehmer nicht oder nicht mehr steuerbar. Sprich,
            die Unfähigkeit kann von ihm nicht "abgestellt" werden. Deshalb entfällt in solchen Fällen auch das Erfordernis einer Abmahnung.
          </p>
          <p>
            Denkbar sind aber auch Fälle, in denen der Arbeitnehmer in der Lage wäre, die fehlende Fähigkeit und Eignung (wieder) zu erlangen. Ein Beispiel
            hierfür:
            <br />
            Der Arbeitnehmer kann seine Arbeit nicht mehr ordnungsgemäß verrichten, weil er den erforderlichen, neuesten Kenntnisstand nicht hat. Eine Schulung
            könnte hier Abhilfe schaffen. In solchen Fällen muss zunächst eine Abmahnung ausgesprochen werden, um den Arbeitnehmer zur Behebung der Unfähigkeit
            zu veranlassen.
          </p>
          <hr />
          <Box sx={guidebookContentStyle.scrollToAnchor} id={"krankheitsbedingte-kuendigung"} />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Krankheitsbedingte Kündigung
          </Typography>
          <p>
            Bei einer krankheitsbedingten Kündigung handelt es sich um einen Sonderfall der personenbedingten Kündigung. Gekündigt wird nicht wegen der
            Krankheit an sich, sondern wegen der betrieblicher Auswirkungen, die die Krankheit mit sich bringt. Wenn der Arbeitnehmer die geschuldete
            Arbeitsleistung krankheitsbedingt nicht erbringen kann, kann der Arbeitgeber das Arbeitsverhältnis beenden.
          </p>
          <p>
            Die krankheitsbedingte Unfähigkeit die Arbeitsleistung zu erbringen ist in vielen Fällen vom Arbeitnehmer nicht oder nicht mehr steuerbar. Deshalb
            entfällt in solchen Fällen auch das Erfordernis einer Abmahnung. Die krankheitsbedingte Kündigung wird in der Regel als ordentliche Kündigung
            ausgesprochen.
          </p>
          <p>Voraussetzungen für eine krankheitsbedingte Kündigung sind:</p>
          <ul>
            <li>Krankheitsbedingter völliger oder teilweiser Ausfall der vertragsmäßigen Arbeitsleistung</li>
            <li>
              Negative Prognose: Ist davon auszugehen, dass der Arbeitnehmer auch in Zukunft seine Arbeitsleistung ganz oder teilweise nicht mehr erbringen
              kann?
            </li>
            <li>Erhebliche Beeinträchtigung betrieblicher oder wirtschaftlicher Interessen durch die krankheitsbedingten Gründe</li>
            <li>Fehlende Weiterbeschäftigungsmöglichkeit: Ist eine Versetzung auf einen anderen Arbeitsplatz möglich und zumutbar?</li>
          </ul>
          <hr />
          <Box sx={guidebookContentStyle.scrollToAnchor} id={"betriebsbedingte-kuendigung"} />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Betriebsbedingte Kündigung
          </Typography>
          <p>
            Eine Kündigung ist sozial gerechtfertigt, wenn sie durch dringende betriebliche Erfordernisse begründet wird, die eine Weiterbeschäftigung des
            Arbeitnehmers verhindern.
          </p>
          <p>Eine betriebsbedingte Kündigung kann vorliegen, wenn</p>
          <ul>
            <li>der Arbeitsplatz im Betrieb infolge dringender betrieblicher Erfordernisse weggefallen ist und</li>
            <li>keine andere Möglichkeit der Weiterbeschäftigung im Unternehmen besteht und</li>
            <li>
              bei der Auswahl des gekündigten Arbeitnehmers die vier Sozialdaten (Dauer der Betriebszugehörigkeit, Lebensalter, Unterhaltspflichten und
              Schwerbehinderung) ausreichend berücksichtigt wurden und
            </li>
            <li>
              eine umfassende Interessenabwägung vorgenommen wurde (sprich, es wurde umfassend überprüft, dass das Interesse des Betriebes gegenüber dem des
              Arbeitnehmers überwiegt)
            </li>
          </ul>
          <hr />
          <Box sx={guidebookContentStyle.scrollToAnchor} id={"aenderungskuendigung"} />
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Änderungskündigung
          </Typography>
          <p>
            Bei einer Änderungskündigung wird das bestehende Arbeitsverhältnis zwar beendet, jedoch gleichzeitig ein neues (anderes) Arbeitsverhältnis
            angeboten. Neben der Beendigung/Kündigung des aktuellen Vertrags, gibt es also das Angebot, einen Arbeitsvertrag zu geänderten Bedingungen
            abzuschließen.
          </p>
          <p>
            Die Beendigung des alten Vertrages und das Änderungsangebot müssen "im zeitlichen Zusammenhang" erfolgen. Ein zeitlicher Zusammenhang zwischen
            Kündigung und Änderungsangebot besteht nur dann, wenn das Änderungsangebot spätestens mit dem Zugang der Kündigungserklärung abgegeben wird. Möglich
            ist daher, zunächst ein Änderungsangebot zu unterbreiten und dann – im Falle der Ablehnung – eine Kündigung folgen zu lassen. Der Arbeitgeber kann
            das Änderungsangebot auch direkt mit der Kündigung verbinden - dies ist das häufigste Vorgehen bei einer Änderungskündigung.
          </p>
          <HighlightBox headline={"Bei allen Kündigungsarten gilt: Wenn nicht innerhalb von 3 Wochen geklagt wurde, ist die Kündigung wirksam!"}>
            <p>
              Wenn Sie nicht innerhalb von 3 Wochen eine Kündigungsschutzklage gegen Ihre Kündigung einreichen, wird sie automatisch wirksam. Dann haben Sie
              keine Möglichkeit mehr, gegen die Kündigung vorzugehen und eine Abfindung zu erhalten, oder auf Ihren Arbeitsplatz zurückzukehren.
            </p>
          </HighlightBox>
          <Typography variant={"h4"} sx={guidebookContentStyle.subheader}>
            Jetzt professionell gegen Ihre Kündigung vorgehen!
          </Typography>
          <p>
            Sie möchten Ihren Arbeitsplatz zurück oder eine Abfindung erhalten? Starten Sie jetzt ganz einfach mit dem ersten Schritt und lassen Sie Ihre
            Kündigung unverbindlich prüfen. Wir sind auf Arbeitsrecht spezialisiert und kümmern uns persönlich um Sie.
          </p>

          <DoubleButton
            buttonText={"Kündigung unverbindlich prüfen"}
            buttonLink={"/servicewelt"}
            backText={"Zurück zum Ratgeber"}
            backLink={"/ratgeber/arbeitsrecht"}
          />
          <QuestionsContactSection />
        </TextContainer>
      </Paper>
    </Layout>
  );
};

export default GuidebookKindsOfNotices;
